import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, Save, Check } from 'lucide-react';
import { supabase } from '@/utils/supabase';

function SectionCard({ title, children }) {
  return (
    <div className="bg-[var(--paper)] border border-[var(--line)] p-5 space-y-4">
      <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)] border-b border-[var(--line)] pb-3">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="font-['Space_Mono'] text-[0.6rem] tracking-[0.14em] uppercase text-[var(--muted)]">
        {label}
      </label>
      {children}
      {hint && <p className="text-[0.58rem] font-['Space_Mono'] text-[var(--muted)]">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, ...rest }) {
  return (
    <input
      value={value}
      onChange={onChange}
      className="field-surface px-3 py-2.5 text-sm w-full focus:outline-none focus:border-[var(--brand)] transition-colors"
      {...rest}
    />
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 transition-colors ${checked ? 'bg-[var(--brand)]' : 'bg-[var(--line)]'}`}
      >
        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
      </div>
      <span className="text-sm text-[var(--ink)] font-['Space_Grotesk']">{label}</span>
    </label>
  );
}

const DEFAULT_SETTINGS = {
  brand: {
    name: 'Smple Product',
    tagline: 'Designed with intent. Made to endure.',
    support_email: 'smpleproduct@gmail.com',
    phone: '',
    social: { instagram: '', twitter: '', facebook: '' },
  },
  announcement: {
    enabled: false,
    text: 'Free shipping on orders over $75',
    link: '',
    bg_color: '#f59e0b',
    text_color: '#09090b',
  },
  shipping: {
    banner: 'Free shipping on orders $75+',
    policy: 'We ship within 2-3 business days.',
    return_policy: '30-day returns on unworn items.',
  },
};

export default function AdminSettings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('site_settings').select('*');
      if (data) {
        const merged = { ...DEFAULT_SETTINGS };
        for (const row of data) {
          if (merged[row.key] !== undefined) {
            merged[row.key] = { ...merged[row.key], ...row.value };
          }
        }
        setSettings(merged);
      }
      setLoading(false);
    })();
  }, []);

  const set = (section, field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSettings((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const setSocial = (network) => (e) => {
    setSettings((prev) => ({
      ...prev,
      brand: { ...prev.brand, social: { ...prev.brand.social, [network]: e.target.value } },
    }));
  };

  const setToggle = (section, field) => (value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      for (const [key, value] of Object.entries(settings)) {
        const { error: uErr } = await supabase
          .from('site_settings')
          .upsert({ key, value }, { onConflict: 'key' });
        if (uErr) throw uErr;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={20} className="animate-spin text-[var(--brand)]" />
    </div>
  );

  const { brand, announcement, shipping } = settings;

  return (
    <div className="p-6 space-y-4 max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-[var(--ink)]">Settings</h1>
          <p className="text-[var(--muted)] text-xs font-['Space_Mono'] mt-0.5">Site-wide configuration</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="btn-brand px-4 py-2.5 flex items-center gap-2 text-[0.65rem] disabled:opacity-60"
        >
          {saved ? <Check size={12} /> : saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {saved ? 'Saved!' : saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs font-['Space_Mono'] p-3 bg-red-400/10 border border-red-400/20">
          <AlertCircle size={12} /><span>{error}</span>
        </div>
      )}

      {/* Brand */}
      <SectionCard title="Brand">
        <Field label="Store Name">
          <Input value={brand.name} onChange={set('brand', 'name')} placeholder="Smple Product" />
        </Field>
        <Field label="Tagline">
          <Input value={brand.tagline} onChange={set('brand', 'tagline')} placeholder="Your tagline…" />
        </Field>
        <Field label="Support Email">
          <Input type="email" value={brand.support_email} onChange={set('brand', 'support_email')} />
        </Field>
        <Field label="Phone">
          <Input value={brand.phone} onChange={set('brand', 'phone')} placeholder="+1 (555) 000-0000" />
        </Field>
      </SectionCard>

      {/* Social Links */}
      <SectionCard title="Social Links">
        <Field label="Instagram">
          <Input value={brand.social?.instagram || ''} onChange={setSocial('instagram')} placeholder="https://instagram.com/…" />
        </Field>
        <Field label="Twitter / X">
          <Input value={brand.social?.twitter || ''} onChange={setSocial('twitter')} placeholder="https://twitter.com/…" />
        </Field>
        <Field label="Facebook">
          <Input value={brand.social?.facebook || ''} onChange={setSocial('facebook')} placeholder="https://facebook.com/…" />
        </Field>
      </SectionCard>

      {/* Announcement Bar */}
      <SectionCard title="Announcement Bar">
        <Toggle
          checked={announcement.enabled}
          onChange={setToggle('announcement', 'enabled')}
          label="Show announcement bar"
        />
        <Field label="Message">
          <Input value={announcement.text} onChange={set('announcement', 'text')} placeholder="Free shipping on orders over $75" />
        </Field>
        <Field label="Link (optional)">
          <Input value={announcement.link} onChange={set('announcement', 'link')} placeholder="/products" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Background Color">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={announcement.bg_color}
                onChange={set('announcement', 'bg_color')}
                className="w-8 h-8 rounded cursor-pointer border border-[var(--line)] bg-transparent"
              />
              <Input value={announcement.bg_color} onChange={set('announcement', 'bg_color')} />
            </div>
          </Field>
          <Field label="Text Color">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={announcement.text_color}
                onChange={set('announcement', 'text_color')}
                className="w-8 h-8 rounded cursor-pointer border border-[var(--line)] bg-transparent"
              />
              <Input value={announcement.text_color} onChange={set('announcement', 'text_color')} />
            </div>
          </Field>
        </div>
      </SectionCard>

      {/* Shipping */}
      <SectionCard title="Shipping & Returns">
        <Field label="Shipping Banner">
          <Input value={shipping.banner} onChange={set('shipping', 'banner')} placeholder="Free shipping on orders $75+" />
        </Field>
        <Field label="Shipping Policy">
          <textarea
            value={shipping.policy}
            onChange={set('shipping', 'policy')}
            rows={3}
            className="field-surface px-3 py-2.5 text-sm w-full resize-none focus:outline-none focus:border-[var(--brand)] transition-colors"
          />
        </Field>
        <Field label="Return Policy">
          <textarea
            value={shipping.return_policy}
            onChange={set('shipping', 'return_policy')}
            rows={3}
            className="field-surface px-3 py-2.5 text-sm w-full resize-none focus:outline-none focus:border-[var(--brand)] transition-colors"
          />
        </Field>
      </SectionCard>
    </div>
  );
}
